import { Mail, Phone } from 'lucide-react'

import { cn } from '@/lib/utils'
import type { Broker } from '@/models/broker'
import { BrokerAvatar } from './BrokerAvatar'

const CONTACT_ROW = 'text-ink-700 flex items-center gap-3 font-mono text-sm'
const CONTACT_LINK = `${CONTACT_ROW} hover:text-ink transition-colors`

interface BrokerContactCardProps {
  broker: Broker
  className?: string
  /** Rendered top-right of the heading — the widget puts its close button here. */
  action?: React.ReactNode
}

/**
 * "Your Cai broker is here to help" — headshot plus contact details, mirroring
 * the broker card on the bound-policy email (`emails/components/broker-card`).
 * Shared by the floating BrokerWidget and the dashboard.
 */
export function BrokerContactCard({ broker, className, action }: BrokerContactCardProps) {
  return (
    <div className={cn('bg-ink-50 rounded-lg p-6', className)}>
      <div className="flex items-start justify-between gap-4">
        <h3 className="text-ink text-lg font-bold">Your Cai broker is here to help</h3>
        {action}
      </div>

      <div className="mt-6 flex items-start gap-5">
        <BrokerAvatar broker={broker} className="size-18 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-ink font-bold">{broker.name}</p>
          <div className="mt-3 flex flex-col gap-2">
            <a href={`mailto:${broker.email}`} className={CONTACT_LINK}>
              <Mail className="text-brand-600 size-4 shrink-0" />
              <span className="truncate">{broker.email}</span>
            </a>
            {/* A number we couldn't parse still gets shown, just not as a
                `tel:` link that would dial something wrong. */}
            {broker.phone &&
              (broker.phoneE164 ? (
                <a href={`tel:${broker.phoneE164}`} className={CONTACT_LINK}>
                  <Phone className="text-brand-600 size-4 shrink-0" />
                  <span className="truncate">{broker.phone}</span>
                </a>
              ) : (
                <span className={CONTACT_ROW}>
                  <Phone className="text-brand-600 size-4 shrink-0" />
                  <span className="truncate">{broker.phone}</span>
                </span>
              ))}
          </div>
        </div>
      </div>
    </div>
  )
}
